// src/SignUp.js
/* global fbq */
/* global ttq */


// src/SignUp.js
import React, { useState, useEffect } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { useNavigate } from 'react-router-dom';
import awsConfig from '../awsConfig';
import { loadStripe } from '@stripe/stripe-js';
import Logo from '../icons/vslogo.png';
import LazyLoadVideo from '../components/LazyLoadVideo.js';

const userPool = new CognitoUserPool({
  UserPoolId: awsConfig.UserPoolId,
  ClientId: awsConfig.ClientId,
});

const stripePublicKey = 'pk_test_51Q0S8vKp1iT8v8bYdTd4UtlnVmoKoPN0BYQ11469GH4KujAApN9rC4qWa19HAz4glmwTfe5ZQuVhKyLpRHr5C7Bp00OtbDUrrL';
const stripePromise = loadStripe(stripePublicKey);

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const priceId = localStorage.getItem('selectedPriceId');
    if (!priceId) {
      navigate('/');
      // Ensure the DOM is ready before scrolling
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing-sec');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [navigate]);

  const redirectToStripeCheckout = async () => {
    const stripe = await stripePromise;
    const priceId = localStorage.getItem('selectedPriceId');
    let clientReferenceId = null;
    if (window.Rewardful && window.Rewardful.referral) {
      clientReferenceId = window.Rewardful.referral;
    }
    fbq('track', 'InitiateCheckout');
  
    if (window.ttq) {
      window.ttq.track('AddToCart', {
        contents: [
          {
            content_id: "101",
            content_type: "product",
            content_name: "StudyBot Unlimited",
            quantity: 1
          }
        ],
        value: 10,
        currency: "USD"
      });
    }
  
    try {
      const response = await fetch('https://api.vidstudio.ai/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          email,
          success_url: window.location.origin + '/success',
          cancel_url: window.location.origin + '/register',
          clientReferenceId,
        }),
      });
  
      const session = await response.json();
      if (session.url) {
        window.location.href = session.url;
      } else {
        alert('There was an issue with your request.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Sorry, there was an error. Please try again.');
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    userPool.signUp(email, password, [], null, async (err, result) => {
      if (err) {
        alert(err.message || JSON.stringify(err));
        return;
      }
      await redirectToStripeCheckout();
    });
  };

  return (
    <div className="flex flex-col min-h-screen lg:flex-row relative">
      {/* Logo at the top left corner */}
      <div className="absolute top-4 left-4">
        <img 
          src={Logo} 
          alt="Logo" 
          className="h-12 w-auto cursor-pointer" 
          onClick={() => navigate('/')}
        />
      </div>

      {/* Sign Up Section - Always visible */}
      <div className="flex-1 flex flex-col justify-center px-6 lg:px-20 xl:px-24 bg-white">
        <div className="w-full max-w-lg">         
          <h3 className="mt-6 text-3xl font-extrabold text-gray-900">Create New Account</h3>
          <p className="mt-2 text-sm text-gray-600">
            Already a user? <a href="/login" className="underline text-blue-500">Log in</a>
          </p>
          <div className="mt-8">
            <div className="mt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  className="w-full px-5 py-3 placeholder-gray-400 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-black"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full px-5 py-3 placeholder-gray-400 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 text-black"
                />
                <button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create Account
                </button>
              </form>
              <div className="mt-6 text-center">
                <a href="#" className="underline text-sm text-gray-600 hover:text-blue-500">
                  By creating an account you agree to our Terms & Conditions
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Showcase Section */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gray-50 p-12 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full opacity-50 blur-3xl"></div>
        <div className="flex flex-col items-center z-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Your creative journey starts <span className="bg-gradient-to-r from-blue-400 to-blue-600 text-transparent bg-clip-text">here</span></h2>
          <p className="text-sm text-gray-600 mb-20">Create your first video in less than 1 minute</p>
          <div className="video-showcase relative flex justify-center items-center h-[400px]">
            {/* Video 1 */}
            <div className="absolute transform -rotate-12 -translate-x-40 z-5">
              <LazyLoadVideo
                className="h-[300px] w-[168px] aspect-[9/16] border-2 border-green-800 rounded-md shadow-lg"
                autoPlay
                loop
                muted
                playsInline
                dataSrc="https://delivery626.nyc3.cdn.digitaloceanspaces.com/compressedvids/0923(7)_comp.mp4"
              />
            </div>
            {/* Video 2 */}
            <div className="z-10">
              <LazyLoadVideo
                className="h-[351px] w-[196px] aspect-[9/16] border-2 border-orange-500 rounded-md shadow-lg"
                autoPlay
                loop
                muted
                playsInline
                dataSrc="https://delivery626.nyc3.cdn.digitaloceanspaces.com/compressedvids/0923(8)_comp.mp4"
              />
            </div>
            {/* Video 3 */}
            <div className="absolute transform rotate-12 translate-x-40 z-5">
              <LazyLoadVideo
                className="h-[300px] w-[168px] aspect-[9/16] border-2 border-cyan-300 rounded-md shadow-lg"
                autoPlay
                loop
                muted
                playsInline
                dataSrc="https://delivery626.nyc3.cdn.digitaloceanspaces.com/compressedvids/0923(3)_comp.mp4"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
